/** Small shared building blocks for the tenant-admin Location Service screens (same look as the other admin pages). */
import { AlertTriangle, Loader2, X } from "lucide-react";

export const inputCls = "w-full rounded-xl bg-muted px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40";

export function PageHead({ title, sub, action }) {
    return (
        <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
                <h2 className="text-xl font-bold">{title}</h2>
                {sub && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{sub}</p>}
            </div>
            {action}
        </div>
    );
}

export function Btn({ children, tone = "primary", className = "", ...rest }) {
    const tones = {
        primary: "bg-primary text-primary-foreground",
        ghost: "border border-border bg-card",
        danger: "bg-destructive text-white",
        dark: "bg-foreground text-background",
    };
    return (
        <button type="button" className={`rounded-xl px-4 min-h-[40px] text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${tones[tone]} ${className}`} {...rest}>
            {children}
        </button>
    );
}

export function Field({ label, children, hint, full }) {
    return (
        <label className={`flex flex-col gap-1 min-w-0 ${full ? "sm:col-span-2" : ""}`}>
            <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
            {children}
            {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
        </label>
    );
}

export function Modal({ title, onClose, children, footer, wide }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
            <div className={`bg-card w-full ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"} rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[92vh]`}>
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
                    <h3 className="font-bold text-lg truncate">{title}</h3>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-2 -mr-2"><X size={20} /></button>
                </div>
                <div className="px-5 py-4 overflow-y-auto">{children}</div>
                {footer && <div className="px-5 py-3 border-t border-border flex justify-end gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>{footer}</div>}
            </div>
        </div>
    );
}

export function Loading() {
    return <div className="py-12 flex justify-center" data-testid="lse-loading"><Loader2 className="animate-spin text-muted-foreground" /></div>;
}

export function Empty({ children, testid = "lse-empty" }) {
    return <div className="mt-4 bg-card border border-dashed border-border rounded-2xl p-8 text-center text-sm text-muted-foreground" data-testid={testid}>{children}</div>;
}

export function ErrorBox({ message, onRetry }) {
    return (
        <div className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 text-sm flex items-center gap-3" data-testid="lse-error">
            <AlertTriangle size={18} className="text-destructive shrink-0" />
            <span className="flex-1">{message || "Couldn't load this page."}</span>
            {onRetry && <Btn tone="ghost" onClick={onRetry}>Retry</Btn>}
        </div>
    );
}

const PILL = {
    green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
    red: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300",
    blue: "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
    muted: "bg-muted text-muted-foreground",
};
export function Pill({ tone = "muted", children }) {
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${PILL[tone] || PILL.muted}`}>{children}</span>;
}

export function Toggle({ checked, onChange, label, testid, disabled }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={!!checked} disabled={disabled}
                   onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
            {label}
        </label>
    );
}

export const errText = (e, fallback = "Something went wrong") => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join("; ");
    if (d?.message) return d.message;
    return fallback;
};

/** Table on wide screens, cards below lg (the admin tables' responsive rule). */
export function ResponsiveList({ rows, columns, card, testid, rowKey = (r) => r.id }) {
    return (
        <div className="mt-4 bg-card border border-border rounded-2xl overflow-hidden" data-testid={testid}>
            <div className="lg:hidden divide-y divide-border">
                {rows.map((r) => <div key={rowKey(r)} className="p-4">{card(r)}</div>)}
            </div>
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>{columns.map((c) => <th key={c.key} className={`px-4 py-2.5 font-bold ${c.className || ""}`}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {rows.map((r) => (
                            <tr key={rowKey(r)}>
                                {columns.map((c) => <td key={c.key} className={`px-4 py-2.5 align-top ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
